import { can } from '@hrms/shared'
import { clickFirst, registerTour, tourTarget } from './registry.ts'

const openFirstTicket = async () => {
  if (!document.querySelector(tourTarget('ticket-side'))) await clickFirst(tourTarget('queue-row'))
}

registerTour({
  id: 'hr-triage',
  title: 'Triage the queue',
  summary: 'Tiles, filters, sorting and working a ticket',
  roles: ['admin'],
  path: '/hr/tickets',
  autoStart: true,
  order: 10,
  steps: [
    {
      element: tourTarget('queue-tiles'),
      popover: {
        title: 'The queue at a glance',
        description:
          'Counts by stage, plus overdue. Click a tile to show only those tickets; click again to clear it.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('queue-filters'),
      popover: {
        title: 'Filters',
        description:
          'Search by key, title or person. Use the filter button to add status, stage, priority, category, people, created date or overdue; pick several values to match any of them. Click a chip to change it, × to remove it. Filters live in the link, so you can share a view.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('queue-sort'),
      popover: {
        title: 'Sort',
        description: 'Click a column heading to sort. ETA puts the most urgent first.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('queue-row'),
      popover: {
        title: 'Open a ticket',
        description: 'Click a row, or Tab to it and press Enter. Next, we’ll open this one.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('hr-quick-actions'),
      prepare: openFirstTicket,
      popover: {
        title: 'Take it on',
        description:
          'Assign to me makes you the owner; Mark resolved closes it out and records the time.',
        side: 'left',
        align: 'start',
      },
    },
    {
      element: tourTarget('hr-status'),
      prepare: openFirstTicket,
      popover: {
        title: 'Status and the waiting prompt',
        description:
          'Change status here. Moving to “Waiting on employee” asks what you need from them, sends it as a reply and pauses the ETA until they answer.',
        side: 'left',
        align: 'start',
      },
    },
    {
      element: tourTarget('composer-mode'),
      prepare: openFirstTicket,
      popover: {
        title: 'Reply or internal note',
        description:
          'Replies go to the employee. Internal notes (yellow) are for the HR team only. Ctrl or Cmd + Enter sends.',
        side: 'top',
        align: 'start',
      },
    },
  ],
})

registerTour({
  id: 'hr-board',
  title: 'The board',
  summary: 'Columns by status, drag to move, ETA hints',
  roles: ['admin'],
  path: '/hr/board',
  autoStart: true,
  order: 20,
  steps: [
    {
      element: tourTarget('board-lanes'),
      popover: {
        title: 'One column per status',
        description:
          'Columns follow the status order in Settings. Each shows its count, most urgent first.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: tourTarget('board-card'),
      popover: {
        title: 'Drag to change status',
        description:
          'Drag a card to another column with the mouse. From the keyboard: focus a card and press Shift + Left or Right. Enter opens it.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: tourTarget('card-eta'),
      popover: {
        title: 'ETA hints',
        description:
          '“Due 29 Oct” is the expected date, “Overdue” (red) has passed it, and “ETA paused” means you’re waiting on the employee.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: tourTarget('queue-filters'),
      popover: {
        title: 'Same filters',
        description: 'The board uses the list’s filter chips, minus status and stage.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('view-switch'),
      popover: {
        title: 'List or board',
        description: 'Switch views any time; your filters come with you.',
        side: 'bottom',
        align: 'end',
      },
    },
  ],
})

const showTab = (id: string) => async () => {
  await clickFirst(tourTarget(`settings-tab-${id}`))
}

registerTour({
  id: 'hr-settings',
  title: 'Settings',
  summary: 'Categories, priorities, statuses and people',
  available: (u) => can(u, 'settings.view'),
  path: '/hr/settings',
  autoStart: true,
  order: 30,
  steps: [
    {
      element: tourTarget('settings-tabs'),
      prepare: showTab('categories'),
      popover: {
        title: 'Four lists',
        description:
          'Categories, priorities (with SLA days), statuses and people. A dot marks a tab with unsaved edits.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: tourTarget('settings-row-actions'),
      prepare: showTab('categories'),
      popover: {
        title: 'Reorder and archive',
        description:
          'Move items up or down. Archiving hides an item from new tickets but keeps it on old ones; nothing is ever deleted.',
        side: 'left',
        align: 'start',
      },
    },
    {
      element: tourTarget('settings-stage'),
      prepare: showTab('statuses'),
      popover: {
        title: 'Stages',
        description:
          'Every status belongs to a stage (New, In progress, Waiting, Done). Stages drive the tiles, the ETA pause and resolved times.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('settings-save'),
      popover: {
        title: 'Save or discard',
        description:
          'Edits stay a draft until you save, and are checked first. Discard throws the draft away.',
        side: 'top',
        align: 'end',
      },
    },
    {
      element: tourTarget('people-access'),
      prepare: showTab('people'),
      popover: {
        title: 'People and access',
        description:
          'Set each person’s access: Employee, HR team or Viewer. Only HR leads can change access or the HR lead flag.',
        side: 'bottom',
        align: 'start',
      },
    },
  ],
})

registerTour({
  id: 'viewer-look',
  title: 'Look around',
  summary: 'Read-only queue and ticket details',
  roles: ['viewer'],
  path: '/hr/tickets',
  autoStart: true,
  order: 10,
  steps: [
    {
      element: tourTarget('queue-tiles'),
      popover: {
        title: 'The HR queue',
        description:
          'You can see every non-confidential ticket. Click a tile to show only that stage.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('queue-filters'),
      popover: {
        title: 'Filters',
        description:
          'Search, or add filter chips with the filter button. Board shows the same tickets by status.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('queue-row'),
      popover: {
        title: 'Open a ticket',
        description: 'Click a row or press Enter on it to read the details.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: tourTarget('viewer-note'),
      prepare: openFirstTicket,
      popover: {
        title: 'View only',
        description:
          'You can read the conversation and activity, but replies, internal notes and changes are for the HR team.',
        side: 'top',
        align: 'start',
      },
    },
  ],
})
